<template>
    <div
        class="control-designer-dockview-root dockview-theme-light"
        style="flex: 1; min-height: 0; display: flex; flex-direction: column"
    >
        <DockviewVue
            class="control-designer-dockview-inner"
            style="flex: 1; min-height: 0; width: 100%"
            :theme="dockTheme"
            default-tab-component="ControlDesignerDockTab"
            :get-tab-context-menu-items="noTabContextMenuItems"
            @ready="onDockReady"
        />
    </div>
</template>

<script>
import { debounce } from 'lodash';
import { themeLight } from 'dockview-core';
import { DockviewVue } from 'dockview-vue';
import 'dockview-vue/dist/styles/dockview.css';
import ControlDesignerDockSlot from './controlDesignerDockSlot.jsx';
import ControlDesignerDockTab from './controlDesignerDockTab.jsx';
import {
    CENTER_TAB_GROUP_PANEL_IDS,
    CODE_PANEL_ID,
    DOCUMENT_TREE_PANEL_ID,
    DESIGNER_PANEL_ID,
    JSON_PANEL_ID,
    JSON_TAB_PANEL,
    LEFT_TAB_GROUP_PANEL_IDS,
    LEFT_TAB_PANELS,
    PROPERTIES_PANEL_ID,
    PROPERTIES_TAB_PANEL,
    RIGHT_TAB_GROUP_PANEL_IDS,
    activatePropertiesPanel,
    activateSiblingOfCodePanel,
    buildControlDesignerDefaultLayout,
    ensureJsonPanelInCenterGroup,
    ensureJsonPanelInPropertiesGroup,
    setGroupVisible,
} from './controlDesignerDockPanelRegistry.js';
import {
    activateDesignerCenterTab,
    migrateControlDesignerDockLayout,
} from './controlDesignerDockLayoutMigrate.js';

export default {
    name: 'control-designer-dock-layout',
    components: {
        DockviewVue,
        ControlDesignerDockSlot,
        ControlDesignerDockTab,
    },
    props: {
        showTree: {
            type: Boolean,
            default: true,
        },
        showDesigner: {
            type: Boolean,
            default: true,
        },
        showCode: {
            type: Boolean,
            default: false,
        },
        treeWidth: {
            type: Number,
            default: 300,
        },
        loadLayout: {
            type: Function,
            required: true,
        },
        saveLayout: {
            type: Function,
            required: true,
        },
    },
    emits: ['dock-ready'],
    provide() {
        const parent = this.$parent;
        return {
            controlDesignerInstance: parent,
            controlDesignerDockRender: parent
                && typeof parent.renderControlDesignerDockPanel === 'function'
                ? (id) => parent.renderControlDesignerDockPanel(id)
                : null,
        };
    },
    data() {
        return {
            dockTheme: themeLight,
            _api: null,
            _layoutDisposable: null,
            _debouncedSave: null,
        };
    },
    watch: {
        showTree: {
            handler() {
                this.syncPanelVisibility();
            },
        },
        showDesigner: {
            handler() {
                this.syncPanelVisibility();
            },
        },
        showCode: {
            handler() {
                this.syncPanelVisibility();
            },
        },
    },
    created() {
        this._debouncedSave = debounce(() => {
            if (!this._api) return;
            try {
                this.saveLayout(this._api.toJSON());
            } catch (e) {
                console.warn('controlDesignerDockLayout: save layout failed', e);
            }
        }, 450);
    },
    beforeUnmount() {
        this._layoutDisposable?.dispose?.();
        this._layoutDisposable = null;
        this._debouncedSave?.cancel?.();
        this._api = null;
    },
    methods: {
        noTabContextMenuItems() {
            return [];
        },
        onDockReady({ api }) {
            this._api = api;

            let restored = false;
            try {
                const saved = this.loadLayout();
                if (saved && typeof saved === 'object' && saved.grid) {
                    api.fromJSON(migrateControlDesignerDockLayout(saved));
                    restored = true;
                }
            } catch (e) {
                console.warn('controlDesignerDockLayout: fromJSON failed, using default', e);
            }
            if (!restored) {
                buildControlDesignerDefaultLayout(api, {
                    showTree: this.showTree,
                    treeWidth: this.treeWidth,
                });
            }

            this.syncPanelVisibility();
            this.ensureLeftTabPanels();
            this.ensureCenterTabPanels();
            this.ensurePropertiesPanel();
            this.syncSingleTabHeaders(api);

            // JSON tab must not be open on refresh / new session (layout may still list it as activeView).
            if (this.showDesigner)
                activateDesignerCenterTab(api);
            else
                activatePropertiesPanel(api);

            this._layoutDisposable?.dispose?.();
            this._layoutDisposable = api.onDidLayoutChange(() => {
                this.syncSingleTabHeaders(api);
                this._debouncedSave();
                if (typeof this.$parent?._throttledDockPanelBump === 'function') {
                    this.$parent._throttledDockPanelBump();
                }
            });

            this.$emit('dock-ready', api);

            this.$nextTick(() => {
                if (typeof this.$parent?._throttledDockPanelBump === 'function') {
                    this.$parent._throttledDockPanelBump();
                }
            });
        },

        syncSingleTabHeaders(api) {
            if (!api) return;
            for (const panel of api.panels) {
                const { group } = panel;
                if (!group?.header) continue;
                const isLeftTabGroup = group.panels.some((p) => LEFT_TAB_GROUP_PANEL_IDS.has(p.id));
                const isCenterTabGroup = group.panels.some((p) => CENTER_TAB_GROUP_PANEL_IDS.has(p.id));
                const isCodeTabGroup = group.panels.some((p) => p.id === CODE_PANEL_ID) && group.panels.length > 1;
                const isPropertiesTabGroup = group.panels.some((p) => p.id === PROPERTIES_PANEL_ID);
                const isRightTabGroup = group.panels.some((p) => RIGHT_TAB_GROUP_PANEL_IDS.has(p.id))
                    && group.panels.some((p) => p.id === JSON_PANEL_ID);
                const isKnownTabGroup = isLeftTabGroup || isCenterTabGroup || isCodeTabGroup
                    || isPropertiesTabGroup || isRightTabGroup;
                group.header.hidden = isKnownTabGroup
                    ? false
                    : group.panels.length <= 1;
            }
        },

        ensureCenterTabPanels() {
            if (!this._api) return;
            if (!this._api.getPanel(DESIGNER_PANEL_ID)) return;
            if (!this._api.getPanel(JSON_TAB_PANEL.id)) {
                this._api.addPanel(JSON_TAB_PANEL.addOptions);
            }
        },

        ensureLeftTabPanels() {
            if (!this._api || !this.showTree) return;

            let treePanel = this._api.getPanel(DOCUMENT_TREE_PANEL_ID);
            if (!treePanel) {
                this._api.addPanel({
                    ...LEFT_TAB_PANELS[0].addOptions,
                    initialWidth: this.treeWidth,
                });
                treePanel = this._api.getPanel(DOCUMENT_TREE_PANEL_ID);
            }

            if (treePanel?.api?.setTitle) {
                treePanel.api.setTitle('Outline');
            }

            if (this.showCode && !this._api.getPanel(CODE_PANEL_ID) && treePanel) {
                this._api.addPanel(LEFT_TAB_PANELS[1].addOptions);
            }
        },

        ensurePropertiesPanel() {
            if (!this._api) return;
            if (!this._api.getPanel(DESIGNER_PANEL_ID)) return;
            if (!this._api.getPanel(PROPERTIES_TAB_PANEL.id)) {
                this._api.addPanel(PROPERTIES_TAB_PANEL.addOptions);
            }
            const propertiesPanel = this._api.getPanel(PROPERTIES_PANEL_ID);
            if (propertiesPanel?.api?.setVisible) {
                propertiesPanel.api.setVisible(true);
            }
        },

        syncCodePanelVisibility() {
            if (!this._api) return;

            const codePanel = this._api.getPanel(CODE_PANEL_ID);
            if (!codePanel) return;

            if (!this.showCode) {
                if (codePanel.group?.activePanel?.id === CODE_PANEL_ID) {
                    activateSiblingOfCodePanel(this._api);
                }
                if (typeof codePanel.api?.setVisible === 'function') {
                    codePanel.api.setVisible(false);
                }
            } else if (typeof codePanel.api?.setVisible === 'function') {
                codePanel.api.setVisible(true);
            }
        },

        syncJsonPanelPlacement() {
            if (!this._api) return;
            if (this.showDesigner)
                ensureJsonPanelInCenterGroup(this._api);
            else
                ensureJsonPanelInPropertiesGroup(this._api);
        },

        syncPanelVisibility() {
            if (!this._api) return;

            if (this.showTree) {
                this.ensureLeftTabPanels();
            }
            this.syncCodePanelVisibility();
            this.ensureCenterTabPanels();
            this.ensurePropertiesPanel();
            this.syncJsonPanelPlacement();

            const treePanel = this._api.getPanel(DOCUMENT_TREE_PANEL_ID);
            if (treePanel) {
                setGroupVisible(treePanel, this.showTree);
            }

            const designerPanel = this._api.getPanel(DESIGNER_PANEL_ID);
            if (designerPanel) {
                setGroupVisible(designerPanel, this.showDesigner);
            }

            this.syncSingleTabHeaders(this._api);
            this.$nextTick(() => {
                if (typeof this.$parent?._throttledDockPanelBump === 'function') {
                    this.$parent._throttledDockPanelBump();
                }
            });
        },
    },
};
</script>

<style>
/* Fallback if any default dockview tab chrome still renders a close affordance. */
.control-designer-dockview-root .dv-default-tab-action {
    display: none !important;
}
</style>
